// Menções no chat interno: @pessoa e @todos.
//
// A mensagem guarda a menção do jeito que se lê ("@João Silva", "@todos"); quem
// foi mencionado vai nas colunas `mencoes` e `menciona_todos` de chat_messages
// (migration 20260924131401). As regras moram aqui, fora da tela, porque valem
// em três momentos — ao enviar, ao editar e ao exibir — e divergir entre eles é
// o erro caro: destacar quem não foi avisado, ou avisar sem destacar.
//
// Menção é coisa de grupo. Em conversa direta a outra pessoa já é a única
// destinatária, e a lista de sugestões ali só atrapalharia.

export type Mencionavel = {
  userId: string;
  /** Nome como é inserido depois do @ — o full_name do perfil. */
  nome: string;
  /** Linha de apoio na lista de sugestões (setor). */
  detalhe?: string | null;
};

export const PALAVRA_TODOS = 'todos';

/**
 * O que pode vir logo antes do @. Começo, espaço, abertura e os marcadores de
 * formatação — `**@Ana**` é menção em negrito. Letra e número não:
 * `fulano@empresa.com` é e-mail.
 */
const ANTES = String.raw`(?<=^|[\s(\[{"'“*_~>])`;

/**
 * O que não pode vir logo depois do nome: letra, número ou outro @. Sem isso,
 * `@Anabela` mencionaria a Ana.
 */
const DEPOIS = String.raw`(?![\p{L}\p{N}_@])`;

function escaparRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Regex que acha as menções num texto; o grupo 1 é o nome depois do @.
 *
 * Nomes mais longos primeiro: com "Ana" e "Ana Paula" no grupo, "@Ana Paula" é
 * a Ana Paula, e não a Ana seguida de "Paula". Maiúscula e minúscula tanto faz;
 * acento não, porque o que a tela destaca precisa ser exatamente o que avisou
 * alguém — e a lista de sugestões já insere o nome com acento.
 */
export function regexDeMencoes(nomes: string[], todos: boolean, global = false): RegExp | null {
  const alternativas = [...new Set(nomes.map(n => n.trim()).filter(Boolean))];
  if (todos) alternativas.push(PALAVRA_TODOS);
  if (!alternativas.length) return null;
  const corpo = alternativas
    .sort((a, b) => b.length - a.length)
    .map(escaparRegex)
    .join('|');
  return new RegExp(`${ANTES}@(${corpo})${DEPOIS}`, global ? 'giu' : 'iu');
}

/** Troca o que está entre crases por espaços: `@fulano` dentro de código é texto. */
function semCodigo(texto: string): string {
  return texto
    .replace(/```[\s\S]*?```/g, m => ' '.repeat(m.length))
    .replace(/`[^`\n]+`/g, m => ' '.repeat(m.length));
}

export type MencoesDaMensagem = { mencoes: string[]; mencionaTodos: boolean };

/**
 * Quem a mensagem menciona, lido do próprio texto.
 *
 * Derivar do texto, em vez de guardar em quem a pessoa clicou na lista, é o que
 * faz rascunho restaurado, nome digitado à mão e edição de mensagem darem o
 * mesmo resultado. Dois homônimos no mesmo grupo são mencionados juntos: o
 * texto não tem como dizer qual dos dois.
 *
 * `podeTodos` já é a resposta completa — grupo, e quem escreve criou o grupo
 * ou é administrador. Sem ela, "@todos" fica sendo só texto.
 */
export function extrairMencoes(
  texto: string,
  mencionaveis: Mencionavel[],
  podeTodos: boolean,
): MencoesDaMensagem {
  const re = regexDeMencoes(mencionaveis.map(m => m.nome), podeTodos, true);
  if (!re) return { mencoes: [], mencionaTodos: false };

  const idsPorNome = new Map<string, string[]>();
  for (const m of mencionaveis) {
    const chave = m.nome.trim().toLowerCase();
    idsPorNome.set(chave, [...(idsPorNome.get(chave) || []), m.userId]);
  }

  const ids = new Set<string>();
  let todos = false;
  for (const achado of semCodigo(texto).matchAll(re)) {
    const chave = achado[1].toLowerCase();
    if (podeTodos && chave === PALAVRA_TODOS) { todos = true; continue; }
    (idsPorNome.get(chave) || []).forEach(id => ids.add(id));
  }
  return { mencoes: [...ids], mencionaTodos: todos };
}

// --- Lista de sugestões -------------------------------------------------------

export type ConsultaDeMencao = {
  /** Posição do @ no texto. */
  inicio: number;
  /** O que foi digitado entre o @ e o cursor. */
  termo: string;
};

/** Depois disso a pessoa claramente não está mais escrevendo um nome. */
const MAX_TERMO = 40;

/**
 * O @ que está sendo digitado agora, se houver: é o que abre a lista.
 *
 * O termo pode ter espaço ("@João Si") porque nome tem espaço. Quem decide que
 * a pessoa desistiu é a lista ficar vazia — por isso o termo não é aparado.
 */
export function consultaDeMencao(texto: string, cursor: number): ConsultaDeMencao | null {
  const antes = texto.slice(0, cursor);
  const arroba = antes.lastIndexOf('@');
  if (arroba === -1) return null;
  if (arroba > 0 && !/[\s(\[{"'“*_~>]/.test(antes[arroba - 1])) return null;
  const termo = antes.slice(arroba + 1);
  if (termo.length > MAX_TERMO || /[\n@]/.test(termo) || /^\s/.test(termo)) return null;
  return { inicio: arroba, termo };
}

/**
 * Como `normalizarTermo` da busca, mas sem aparar o fim: o espaço depois de um
 * nome completo é o sinal de que a menção terminou e a lista deve fechar.
 */
function normalizarSemAparar(valor: string): string {
  return valor.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/**
 * Pessoas que batem com o termo, sem acento: "joao" acha "João". Vale o começo
 * do nome ou o começo de qualquer palavra dele — "@silva" acha "João Silva".
 * Quem bate pelo começo do nome vem antes.
 */
export function filtrarMencionaveis<T extends Mencionavel>(lista: T[], termo: string, limite = 8): T[] {
  const t = normalizarSemAparar(termo);
  const peloInicio: T[] = [];
  const peloMeio: T[] = [];
  for (const item of lista) {
    const n = normalizarSemAparar(item.nome.trim());
    if (n.startsWith(t)) peloInicio.push(item);
    else if (n.includes(` ${t}`)) peloMeio.push(item);
  }
  const porNome = (a: T, b: T) => a.nome.localeCompare(b.nome, 'pt-BR');
  return [...peloInicio.sort(porNome), ...peloMeio.sort(porNome)].slice(0, limite);
}

/** A opção @todos aparece enquanto o termo ainda pode virar "todos". */
export function todosCabeNoTermo(termo: string): boolean {
  return PALAVRA_TODOS.startsWith(normalizarSemAparar(termo));
}

/** Troca o @termo pelo nome escolhido, com um espaço depois, e diz onde fica o cursor. */
export function inserirMencao(
  texto: string,
  consulta: ConsultaDeMencao,
  cursor: number,
  nome: string,
): { texto: string; cursor: number } {
  const antes = texto.slice(0, consulta.inicio);
  const depois = texto.slice(cursor).replace(/^ /, '');
  const inserido = `@${nome} `;
  return { texto: antes + inserido + depois, cursor: antes.length + inserido.length };
}

// --- Exibição e alerta --------------------------------------------------------

type ComMencoes = { mencoes?: string[] | null; menciona_todos?: boolean | null };

export type MencoesDoTexto = {
  /** Quem foi mencionado, com o nome como aparece depois do @. */
  pessoas: { id: string; nome: string }[];
  todos: boolean;
  /** O que é para quem está lendo, em minúsculas: sai em destaque forte. */
  paraVoce: string[];
  /**
   * A mensagem é de quem está lendo. O balão próprio é vinho, e a menção
   * precisa de outra cor para não sumir nele.
   */
  propria?: boolean;
};

/**
 * O que o balão precisa para destacar as menções. Mensagem própria não tem
 * destaque forte: ninguém precisa ser avisado do que acabou de escrever.
 */
export function mencoesParaExibir(
  msg: ComMencoes & { sender_id: string },
  userId: string,
  nomeDe: (id: string) => string | null | undefined,
): MencoesDoTexto | undefined {
  const ids = msg.mencoes || [];
  const todos = !!msg.menciona_todos;
  if (!ids.length && !todos) return undefined;

  const pessoas: { id: string; nome: string }[] = [];
  for (const id of ids) {
    const nome = nomeDe(id)?.trim();
    if (nome) pessoas.push({ id, nome });
  }
  const propria = msg.sender_id === userId;
  const paraVoce: string[] = [];
  if (!propria) {
    const meu = ids.includes(userId) ? nomeDe(userId)?.trim() : null;
    if (meu) paraVoce.push(meu.toLowerCase());
    if (todos) paraVoce.push(PALAVRA_TODOS);
  }
  return { pessoas, todos, paraVoce, propria };
}

export type TipoDeMencao = 'voce' | 'todos';

/** A mensagem menciona quem está logado? Menção direta vence o @todos. */
export function mencaoPara(msg: ComMencoes, userId: string): TipoDeMencao | null {
  if (msg.mencoes?.includes(userId)) return 'voce';
  if (msg.menciona_todos) return 'todos';
  return null;
}

/**
 * O banco ainda não tem as colunas de menção?
 *
 * O front e a migration saem no mesmo merge, mas por workflows diferentes. Se o
 * front chegar antes — ou a migration falhar e ficar parada —, gravar
 * `mencoes` é recusado, e a mensagem não pode deixar de sair por isso: quem
 * envia tenta de novo sem as colunas. PGRST204 é o PostgREST dizendo que não
 * conhece a coluna; 42703 é o Postgres dizendo o mesmo.
 */
export function faltaColunaDeMencao(erro: { code?: string | null; message?: string | null } | null): boolean {
  if (!erro) return false;
  if (erro.code !== 'PGRST204' && erro.code !== '42703') return false;
  return /menc/i.test(erro.message || '');
}

/** Título da notificação de menção: "Ana mencionou você · Fiscal". */
export function tituloDaMencao(remetente: string, tipo: TipoDeMencao, conversa?: string | null): string {
  const base = `${remetente} mencionou ${tipo === 'voce' ? 'você' : 'todos'}`;
  return conversa ? `${base} · ${conversa}` : base;
}
